<template>
  <div>
    <Head title="Channel Pricing" />
    <h1 class="mb-8 text-3xl font-bold">Channel Pricing</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset" />
      <Link class="btn-orange" href="/channel-pricings/create">
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;Channel Price</span>
      </Link>
      <a class="btn-orange" href="#" @click="showUploadPrompt">
        <span>Upload</span>
        <span class="hidden md:inline">&nbsp;Prices</span>
      </a>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Part Number</th>
          <th class="pb-3 pt-3 px-3">Part Description</th>
          <th class="pb-3 pt-3 px-3">Channel</th>
          <th class="pb-3 pt-3 px-3">Price (VAT Exclusive)</th>
          <th class="pb-3 pt-3 px-3">Price (VAT Inclusive)</th>
          <th class="pb-3 pt-3 px-3" />
        </tr>
        <tr v-for="item in pricings.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/channel-pricings/${item.id}/edit`">
              {{ item.part_no }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/channel-pricings/${item.id}/edit`">
              {{ item.description }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/channel-pricings/${item.id}/edit`">
              {{ item.channel }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/channel-pricings/${item.id}/edit`">
              {{ item.price_exclusive }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/channel-pricings/${item.id}/edit`">
              {{ item.price_inclusive }}
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-3" :href="`/channel-pricings/${item.id}/edit`" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="pricings.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="5">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="pricings.links" />
  </div>
  <div v-if="showUpload" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white w-1/2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">Upload Prices File</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form enctype="multipart/form-data" @submit.prevent="store">
                <div class="flex flex-wrap -mb-2 -mr-6 pl-8 pr-8 pt-8 pb-2">
                  <file-input v-model="formData.price_file" :error="$props.errors.price_file" class="pb-2 pr-6 w-full lg:w-full" type="file" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" label="Prices Excel File" />
                  <p v-if="$props.errors.price_file"><small class="text-red-700">{{ $props.errors.price_file }}</small></p>
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="store">
                Process
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
  
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import FileInput from '@/Shared/FileInput'
  
export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
    FileInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    pricings: Object,
    errors: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
      },
      formData: {
        price_file: null,
      },
      showUpload: false,
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/channel-pricings', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    showUploadPrompt() {
      this.showUpload = true
    },
    closeModal() {
      this.showUpload = false
    },
    store() {
      this.$inertia.post('/upload-prices', this.formData, {
        onSuccess: () => {
          this.formData.price_file = null
          this.showUpload = false
        },
      })
    },
  },
}
</script>
  